import { useEffect, useState } from "react";
import { api, fetchLibrary } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Uploader from "@/components/Uploader";
import { Radio, Trash2, Plus, Edit, ExternalLink, Sparkles } from "lucide-react";

export default function MyChannel() {
  const { user } = useAuth() || {};
  const nav = useNavigate();
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const chs = await api.get("/me/channels").then(r => r.data);
      setChannels(chs);
      if (chs.length && !selected) setSelected(chs[0].slug);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (!selected) { setContent([]); return; }
    const ch = channels.find(c => c.slug === selected);
    if (!ch) return;
    fetchLibrary({ channel_id: ch.id }).then(setContent);
  }, [selected, channels]);

  if (loading) return <div className="pt-24 text-center text-white/60">Loading…</div>;

  if (!channels.length) {
    return (
      <div className="pt-24 max-w-2xl mx-auto text-center px-6" data-testid="my-channel-empty">
        <Radio className="w-10 h-10 text-[#D4AF37] mx-auto mb-4"/>
        <h1 className="font-heading text-3xl font-bold mb-3">You don't own a channel yet</h1>
        <p className="text-white/60 mb-6">Purchase a channel to broadcast your ministry on VOTLiving.tv.</p>
        <Link to="/purchase-channel" className="inline-block btn-gold px-6 py-3 rounded-full text-sm font-semibold" data-testid="cta-purchase-channel">Purchase a Channel →</Link>
      </div>
    );
  }

  const ch = channels.find(c => c.slug === selected) || channels[0];

  return (
    <div className="pt-16" data-testid="my-channel-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3"/>Channel Manager</p>
        <h1 className="font-heading text-4xl font-bold mb-6">My Channels</h1>

        <div className="flex gap-2 flex-wrap mb-8">
          {channels.map(c => (
            <button key={c.slug} onClick={()=>setSelected(c.slug)} className={`px-4 py-2 rounded-full text-sm ${selected===c.slug ? "bg-[#D4AF37] text-[#050507] font-semibold" : "bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"}`} data-testid={`select-channel-${c.slug}`}>
              {c.name} {c.status && c.status !== "active" && <span className="ml-2 text-[9px] tracking-widest uppercase opacity-70">{c.status}</span>}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-10">
          <ChannelSettings channel={ch} onSaved={loadAll} />
          <ScheduleManager channel={ch} onChanged={loadAll} />
        </div>

        <ContentManager channel={ch} content={content} onChanged={() => fetchLibrary({ channel_id: ch.id }).then(setContent)} />

        <div className="mt-8 text-center">
          <Link to={`/live/${ch.slug}`} className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline text-sm" data-testid="view-live-link"><ExternalLink className="w-4 h-4"/>View your channel live</Link>
        </div>
      </div>
    </div>
  );
}

function ChannelSettings({ channel, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({ name: channel.name || "", tagline: channel.tagline || "", logo_url: channel.logo_url || "", stream_url: channel.stream_url || "", stream_type: channel.stream_type || "hls", color: channel.color || "#D4AF37" }); }, [channel.slug]);
  const save = async () => {
    try { await api.put(`/me/channels/${channel.slug}`, f); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid="channel-settings">
      <h3 className="font-heading text-xl font-bold mb-4">Channel Settings</h3>
      <div className="space-y-3">
        <Input placeholder="Name" value={f.name} onChange={v=>setF({...f,name:v})} testid="cs-name"/>
        <Input placeholder="Tagline" value={f.tagline} onChange={v=>setF({...f,tagline:v})} testid="cs-tagline"/>
        <div className="flex gap-2 items-center">
          <input placeholder="Logo URL" value={f.logo_url || ""} onChange={e=>setF({...f,logo_url:e.target.value})} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="cs-logo"/>
          <Uploader accept="image/*" label="Upload" testid="cs-logo-upload" onUploaded={u => setF(p => ({...p, logo_url: u.publicUrl}))}/>
        </div>
        <div className="flex gap-2 items-center">
          <input placeholder="Stream URL (.m3u8 or .mp4)" value={f.stream_url || ""} onChange={e=>setF({...f,stream_url:e.target.value})} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="cs-stream"/>
          <Uploader accept="video/*" label="Upload" testid="cs-stream-upload" onUploaded={u => setF(p => ({...p, stream_url: u.publicUrl, stream_type: "mp4"}))}/>
        </div>
        <select value={f.stream_type || "hls"} onChange={e=>setF({...f,stream_type:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cs-type">
          <option value="hls">HLS (.m3u8)</option><option value="mp4">MP4</option>
        </select>
        <button onClick={save} className="btn-gold px-5 py-2 rounded-full text-sm font-semibold" data-testid="cs-save">Save changes</button>
      </div>
    </div>
  );
}

function ScheduleManager({ channel, onChanged }) {
  const [prog, setProg] = useState({ title: "", description: "", start_time: "", duration_min: 30, host: "" });
  const add = async () => {
    if (!prog.title || !prog.start_time) return toast.error("Title + start required");
    try { await api.post(`/me/channels/${channel.slug}/programs`, { ...prog, duration_min: Number(prog.duration_min) }); toast.success("Program added"); setProg({title:"",description:"",start_time:"",duration_min:30,host:""}); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (pid) => { await api.delete(`/me/channels/${channel.slug}/programs/${pid}`); toast.success("Removed"); onChanged(); };
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid="schedule-manager">
      <h3 className="font-heading text-xl font-bold mb-4">Programming Schedule</h3>
      <div className="space-y-1 max-h-56 overflow-y-auto mb-4">
        {(channel.schedule || []).length === 0 && <p className="text-white/50 text-sm">No programs yet.</p>}
        {(channel.schedule || []).map(p => (
          <div key={p.id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded" data-testid={`prog-${p.id}`}>
            <span><span className="text-white/50">{new Date(p.start_time).toLocaleString([],{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</span> · {p.title} · <span className="text-white/50">{p.duration_min}m</span></span>
            <button onClick={()=>remove(p.id)} className="text-white/40 hover:text-[#E11D48]"><Trash2 className="w-3.5 h-3.5"/></button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <Input placeholder="Program title" value={prog.title} onChange={v=>setProg({...prog,title:v})} testid="prog-title"/>
        <Input placeholder="Host (optional)" value={prog.host} onChange={v=>setProg({...prog,host:v})} testid="prog-host"/>
        <Input placeholder="Start ISO (2026-03-01T14:00)" value={prog.start_time} onChange={v=>setProg({...prog,start_time:v})} testid="prog-start"/>
        <Input placeholder="Duration (min)" value={prog.duration_min} onChange={v=>setProg({...prog,duration_min:v})} testid="prog-dur"/>
      </div>
      <button onClick={add} className="btn-gold rounded-full text-sm px-4 py-1.5 flex items-center gap-2" data-testid="add-prog"><Plus className="w-3.5 h-3.5"/>Add program</button>
    </div>
  );
}

function ContentManager({ channel, content, onChanged }) {
  const [f, setF] = useState({ title: "", description: "", thumbnail: "", poster: "", duration_min: 30, category: "On Demand", year: 2026, rating: "TV-G", video_url: "", stream_type: "mp4" });
  const add = async () => {
    if (!f.title || !f.video_url) return toast.error("Title and video URL required (upload or paste)");
    try {
      await api.post("/me/content", { ...f, channel_id: channel.id, poster: f.poster || f.thumbnail, duration_min: Number(f.duration_min), year: Number(f.year) });
      toast.success("Content added"); setF({ title:"", description:"", thumbnail:"", poster:"", duration_min:30, category:"On Demand", year:2026, rating:"TV-G", video_url:"", stream_type:"mp4" }); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (cid) => { if (!window.confirm("Delete this content?")) return; await api.delete(`/me/content/${cid}`); toast.success("Deleted"); onChanged(); };
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid="content-manager">
      <h3 className="font-heading text-xl font-bold mb-4">Video Library — self-service uploads</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="space-y-2">
          <Input placeholder="Title" value={f.title} onChange={v=>setF({...f,title:v})} testid="ct-title"/>
          <textarea placeholder="Description" rows={2} value={f.description} onChange={e=>setF({...f,description:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="ct-desc"/>
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Duration (min)" value={f.duration_min} onChange={v=>setF({...f,duration_min:v})} testid="ct-dur"/>
            <Input placeholder="Year" value={f.year} onChange={v=>setF({...f,year:v})} testid="ct-year"/>
            <Input placeholder="Rating (TV-G)" value={f.rating} onChange={v=>setF({...f,rating:v})} testid="ct-rating"/>
          </div>
          <Input placeholder="Category (e.g., Sermons)" value={f.category} onChange={v=>setF({...f,category:v})} testid="ct-cat"/>
        </div>
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <input placeholder="Thumbnail URL" value={f.thumbnail} onChange={e=>setF({...f,thumbnail:e.target.value})} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="ct-thumb"/>
            <Uploader accept="image/*" label="Upload" testid="ct-thumb-upload" onUploaded={u => setF(p => ({...p, thumbnail: u.publicUrl}))}/>
          </div>
          <div className="flex gap-2 items-center">
            <input placeholder="Video URL (mp4 or m3u8)" value={f.video_url} onChange={e=>setF({...f,video_url:e.target.value})} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]/60" data-testid="ct-video"/>
            <Uploader accept="video/*" label="Upload" testid="ct-video-upload" onUploaded={u => setF(p => ({...p, video_url: u.publicUrl, stream_type: "mp4"}))}/>
          </div>
          <select value={f.stream_type} onChange={e=>setF({...f,stream_type:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="ct-type"><option value="mp4">MP4</option><option value="hls">HLS</option></select>
          <button onClick={add} className="btn-gold rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-content"><Plus className="w-4 h-4"/>Add to library</button>
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-widest uppercase text-white/40 mb-2">Existing videos ({content.length})</p>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {content.map(c => (
            <div key={c.id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded" data-testid={`content-${c.id}`}>
              <span><span className="font-medium">{c.title}</span> <span className="text-white/50">· {c.category} · {c.duration_min}m</span></span>
              <button onClick={()=>remove(c.id)} className="text-white/40 hover:text-[#E11D48]"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Input({ testid, ...props }) {
  return <input {...props} onChange={e=>props.onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid={testid} />;
}
